/**
 * FSSAI licence display page.
 *
 *   - The licence JPG is served directly from the user-uploaded asset CDN
 *     so it never expires from GridFS / can be swapped without redeploys.
 *   - The page mirrors the same outer chrome (PolicyLayout cousin) used by
 *     /terms-and-conditions etc. so styling stays consistent.
 *   - Mobile: the document is rendered with `max-w-full h-auto` plus a
 *     subtle border so it reads cleanly on phones; tapping opens the
 *     full-res file in a new tab.
 *
 * NOTE FOR FUTURE: if a multi-page PDF is uploaded later, swap the <img>
 * for an <iframe src=…#toolbar=0 /> or render each page in sequence.
 */
import { CONTACT, useContactNumber } from "../lib/contact";
import SEOHead from "../components/SEOHead";

const FSSAI_DOC_URL = "https://customer-assets.emergentagent.com/job_spice-bazaar-124/artifacts/r4kwvz4h_SRI_DURGA_FOODS_FSSAI_LICENSE.jpg";
const FSSAI_NUMBER = "10121009000420";

export default function FSSAIPage() {
  // iter53 — live admin-editable call number.
  const contact = useContactNumber();
  return (
    <div className="container-x py-12 lg:py-20" data-testid="page-fssai">
      <SEOHead
        title="FSSAI Licence | Sri Durga Foods"
        description="Sri Durga Foods operates under FSSAI State Licence 10121009000420 — issued in Nellore, Andhra Pradesh for authentic pickles, masalas, spices and wood-pressed oils."
        canonical="/fssai"
      />
      <div className="max-w-3xl mx-auto">
        <span className="overline mb-3 block">Sri Durga Foods</span>
        <h1 className="font-serif text-4xl sm:text-5xl text-brand-dark mb-3">FSSAI Licence</h1>
        <p className="text-sm text-brand-muted mb-3">
          Issued by the Government of Andhra Pradesh under the Food Safety and
          Standards Authority of India (FSS Act, 2006).
        </p>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8 text-xs">
          <div className="bg-brand-subtle rounded-lg px-3 py-2">
            <div className="text-brand-muted">Licence No.</div>
            <div className="font-medium text-brand-dark mt-0.5" data-testid="fssai-number">{FSSAI_NUMBER}</div>
          </div>
          <div className="bg-brand-subtle rounded-lg px-3 py-2">
            <div className="text-brand-muted">Category</div>
            <div className="font-medium text-brand-dark mt-0.5">State Licence</div>
          </div>
          <div className="bg-brand-subtle rounded-lg px-3 py-2">
            <div className="text-brand-muted">Issued On</div>
            <div className="font-medium text-brand-dark mt-0.5">24 Apr 2024</div>
          </div>
          <div className="bg-brand-subtle rounded-lg px-3 py-2">
            <div className="text-brand-muted">Valid Upto</div>
            <div className="font-medium text-brand-dark mt-0.5">09 Jul 2029</div>
          </div>
        </div>

        {/* Document viewer ──────────────────────────────────────────── */}
        <div className="border border-brand-line rounded-xl overflow-hidden bg-brand-cream shadow-sm">
          <img
            src={FSSAI_DOC_URL}
            alt="Sri Durga Foods FSSAI Licence — Form C, Government of Andhra Pradesh"
            loading="eager"
            className="w-full h-auto select-none"
            data-testid="fssai-document-image"
            draggable="false"
          />
        </div>

        <div className="border-t border-brand-line pt-7 mt-10 text-[15px] leading-relaxed text-brand-dark/85 space-y-3">
          <p>
            Sri Durga Foods operates under FSSAI State Licence{" "}
            <strong className="text-brand-dark">{FSSAI_NUMBER}</strong>,
            issued for our manufacturing and re-packing unit in SPSR
            Nellore, Andhra Pradesh. All our pickles, pastes, spices,
            masalas, traditional powders, flours and wood-pressed oils are
            produced in compliance with the FSS Act, 2006 and FSSAI hygiene
            standards.
          </p>
          <p className="text-sm text-brand-muted">
            For any verification queries please contact us at{" "}
            <a className="text-brand-chili hover:underline" href={`mailto:${CONTACT.EMAIL}`}>{CONTACT.EMAIL}</a>{" "}
            or call{" "}
            <a className="text-brand-chili hover:underline" href={contact.tel}>{contact.display}</a>.
          </p>
        </div>
      </div>
    </div>
  );
}
